import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Plus, Code, Trash2, Copy, Database, Wand2, Loader2, X, FileCode } from 'lucide-react';

const FIELD_TYPES = [
  { value: 'string', label: 'Texto corto' },
  { value: 'text', label: 'Texto largo' },
  { value: 'number', label: 'Numero' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'date', label: 'Fecha' },
  { value: 'url', label: 'URL' },
  { value: 'email', label: 'Email' },
];

export default function ApiGeneratorPage() {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ slug: '', name: '', description: '', fields: [{ key: '', label: '', type: 'string', required: false }] });
  const [saving, setSaving] = useState(false);
  const [samplesFor, setSamplesFor] = useState(null);
  const [samples, setSamples] = useState(null);
  const [sampleTab, setSampleTab] = useState('curl');

  const load = useCallback(() => {
    api.get('/api-gen/entities').then(r => { setEntities(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ slug: '', name: '', description: '', fields: [{ key: '', label: '', type: 'string', required: false }] });
    setDialogOpen(true);
  };

  const openEdit = (e) => {
    setEditing(e.id);
    setForm({ slug: e.slug, name: e.name, description: e.description || '', fields: e.fields.length ? e.fields : [{ key: '', label: '', type: 'string', required: false }] });
    setDialogOpen(true);
  };

  const addField = () => setForm(f => ({ ...f, fields: [...f.fields, { key: '', label: '', type: 'string', required: false }] }));
  const removeField = (i) => setForm(f => ({ ...f, fields: f.fields.filter((_, idx) => idx !== i) }));
  const updateField = (i, patch) => setForm(f => ({ ...f, fields: f.fields.map((fld, idx) => idx === i ? { ...fld, ...patch } : fld) }));

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/api-gen/entities/${editing}`, form);
        toast.success('Entidad actualizada');
      } else {
        await api.post('/api-gen/entities', form);
        toast.success('Entidad creada');
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Eliminar entidad "${e.name}"? Se borraran todos los datos (${e.item_count} items).`)) return;
    try {
      await api.delete(`/api-gen/entities/${e.id}`);
      toast.success('Eliminada');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const viewSamples = async (e) => {
    setSamplesFor(e);
    setSamples(null);
    try {
      const r = await api.get(`/api-gen/entities/${e.id}/samples`);
      setSamples(r.data);
    } catch { toast.error('Error cargando samples'); setSamplesFor(null); }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="api-gen-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-white flex items-center gap-2">
            <Wand2 className="w-6 h-6 text-primary" /> API Generator
          </h2>
          <p className="text-muted-foreground text-sm mt-1">Crea endpoints CRUD personalizados sin tocar codigo.</p>
        </div>
        <Button onClick={openNew} className="bg-primary hover:bg-primary/90 text-black font-bold" data-testid="apigen-new-btn">
          <Plus className="w-4 h-4 mr-2" /> Nueva entidad
        </Button>
      </div>

      {entities.length === 0 ? (
        <Card className="bg-card/50 backdrop-blur-md border-white/5">
          <CardContent className="py-16 text-center">
            <Database className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-white font-semibold mb-2">Sin entidades todavia</p>
            <p className="text-muted-foreground text-sm mb-4 max-w-md mx-auto">
              Crea una entidad (ej. "Visitas", "Citas", "Encuestas") con sus campos, y automaticamente se generaran endpoints CRUD REST con codigo de integracion.
            </p>
            <Button onClick={openNew} className="bg-primary hover:bg-primary/90 text-black font-bold" data-testid="apigen-empty-btn">
              <Plus className="w-4 h-4 mr-2" /> Crear primera entidad
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {entities.map(e => (
            <Card key={e.id} className="bg-card/50 backdrop-blur-md border-white/5 hover:border-primary/20" data-testid={`apigen-entity-${e.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Database className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-white text-sm">{e.name}</p>
                      <code className="text-[10px] text-muted-foreground">/api/custom/{e.slug}</code>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[9px] border-primary/30 text-primary">{e.item_count || 0} items</Badge>
                </div>
                {e.description && <p className="text-[11px] text-muted-foreground leading-relaxed mb-3">{e.description}</p>}
                <div className="flex flex-wrap gap-1 mb-3">
                  {e.fields.map(f => (
                    <Badge key={f.key} variant="outline" className="text-[9px] border-white/10 text-white/70">
                      {f.label}: <span className="ml-1 text-primary/80">{f.type}</span>{f.required && <span className="text-rose-400">*</span>}
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => viewSamples(e)} className="flex-1 bg-primary/10 text-primary hover:bg-primary/20 font-bold" data-testid={`apigen-samples-${e.id}`}>
                    <FileCode className="w-3.5 h-3.5 mr-1" /> Ver codigo
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(e)} className="border-white/10" data-testid={`apigen-edit-${e.id}`}>
                    Editar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(e)} className="text-rose-400 hover:bg-rose-500/10" data-testid={`apigen-delete-${e.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Entity form */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#1F2833] border-white/10" data-testid="apigen-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2">
              <Database className="w-5 h-5 text-primary" /> {editing ? 'Editar entidad' : 'Nueva entidad'}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">
              Tras guardar, tendras endpoints REST automaticos en /api/custom/&#123;slug&#125;.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Nombre visible *</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Visitas" className="bg-black/20 border-white/10" data-testid="apigen-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Slug (url) *</Label>
                <Input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase() }))} placeholder="visitas" disabled={!!editing} className="bg-black/20 border-white/10 font-mono" data-testid="apigen-slug-input" />
                <p className="text-[10px] text-muted-foreground">Solo a-z, 0-9, _ · no editable despues</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descripcion (opcional)</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className="bg-black/20 border-white/10" data-testid="apigen-desc-input" />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Campos ({form.fields.length})</Label>
                <Button size="sm" variant="outline" onClick={addField} className="border-white/10 text-white/80 h-7 text-xs" data-testid="apigen-add-field-btn">
                  <Plus className="w-3 h-3 mr-1" /> Campo
                </Button>
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto p-2 rounded-lg border border-white/5 bg-black/10">
                {form.fields.map((f, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center p-2 rounded-lg bg-black/20 border border-white/5" data-testid={`apigen-field-${i}`}>
                    <Input placeholder="clave" value={f.key} onChange={e => updateField(i, { key: e.target.value.toLowerCase() })} className="col-span-3 bg-black/30 border-white/10 text-xs font-mono" data-testid={`apigen-field-key-${i}`} />
                    <Input placeholder="Etiqueta" value={f.label} onChange={e => updateField(i, { label: e.target.value })} className="col-span-3 bg-black/30 border-white/10 text-xs" />
                    <Select value={f.type} onValueChange={v => updateField(i, { type: v })}>
                      <SelectTrigger className="col-span-3 bg-black/30 border-white/10 text-xs" data-testid={`apigen-field-type-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FIELD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="col-span-2 flex items-center gap-1 justify-center">
                      <Switch checked={f.required} onCheckedChange={v => updateField(i, { required: v })} data-testid={`apigen-field-required-${i}`} />
                      <span className="text-[10px] text-muted-foreground">Req.</span>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => removeField(i)} disabled={form.fields.length <= 1} className="col-span-1 h-8 w-8 p-0 text-rose-400" data-testid={`apigen-field-remove-${i}`}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <Button onClick={save} disabled={saving} className="w-full bg-primary hover:bg-primary/90 text-black font-bold" data-testid="apigen-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
              {editing ? 'Actualizar' : 'Crear'} entidad
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Samples dialog */}
      <Dialog open={!!samplesFor} onOpenChange={v => { if (!v) { setSamplesFor(null); setSamples(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#1F2833] border-white/10" data-testid="apigen-samples-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2">
              <Code className="w-5 h-5 text-primary" /> Codigo para {samplesFor?.name}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">
              Copia el snippet que necesites para integrar con sistemas externos.
            </DialogDescription>
          </DialogHeader>
          {!samples ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-xs font-semibold text-white mb-2">Endpoints disponibles:</p>
                <div className="space-y-1 text-[11px] font-mono">
                  {Object.entries(samples.endpoints).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2 p-2 rounded bg-black/20 border border-white/5">
                      <Badge variant="outline" className="text-[9px] border-white/10 text-primary uppercase w-16 justify-center">{k}</Badge>
                      <code className="text-[10px] text-white/70 truncate">{v}</code>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  {['curl', 'javascript', 'python'].map(t => (
                    <button
                      key={t}
                      onClick={() => setSampleTab(t)}
                      className={`text-[11px] px-3 py-1 rounded ${sampleTab === t ? 'bg-primary text-black font-bold' : 'bg-white/5 text-white/60'}`}
                      data-testid={`apigen-sample-tab-${t}`}
                    >
                      {t}
                    </button>
                  ))}
                  <Button size="sm" variant="ghost" onClick={() => copyText(samples[`${sampleTab}_create`])} className="ml-auto h-7 text-xs text-primary" data-testid="apigen-sample-copy-btn">
                    <Copy className="w-3 h-3 mr-1" /> Copiar
                  </Button>
                </div>
                <pre className="text-[10px] text-white/80 bg-black/40 p-3 rounded overflow-x-auto max-h-80" data-testid="apigen-sample-code"><code>{samples[`${sampleTab}_create`]}</code></pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
